/**
 * Asset-type → metric-name catalog.
 *
 * Mirrors the rule arrays in `backend/src/services/telemetry-mapper.service.ts`.
 * Keep in sync with backend changes — there is no runtime bridge, but the test
 * suite and the backend-shape test (`telemetry-mapper.service.test.ts`) both
 * guard against the most common drift patterns.
 *
 * Keys are the cloud-side asset_type values written to TimescaleDB tags by
 * DEVICE_TYPE_TO_ASSET in the backend mapper, plus a "site" bucket for
 * site-summary metrics.
 */

export type AssetType = 'battery_storage' | 'solar_pv' | 'generator' | 'meter' | 'site';

const COMMON_METRIC_NAMES = [
  'active_power_kw',
  'reactive_power_kvar',
  'voltage_v',
  'current_a',
  'frequency',
  'apparent_power_kva',
  'power_factor',
  'max_reactive_capacity',
  'min_reactive_capacity',
  'device_count',
  'faulted_count',
  'block_efficiency',
  'comm_success_rate',
  'device_online',
] as const;

const BATTERY_METRIC_NAMES = [
  'state_of_charge',
  'battery_temperature',
  'cell_voltage_min',
  'cell_voltage_max',
  'max_power_kw',
  'min_power_kw',
  'state_of_health',
  'pack_voltage',
  'pack_current',
  'max_charge_power',
  'max_discharge_power',
  'cell_temp_min',
  'cell_temp_max',
  'dc_bus_voltage',
  'dc_bus_current',
  'rack_count_total',
  'rack_count_online',
  'cells_balancing',
  'pcs_ac_voltage',
  'pcs_ac_current',
  'pcs_ac_frequency',
  // BMS comms-loss (0/1) — 0 = "BMS comms down" (djin-cloud#559). Kept in lockstep
  // with the backend ENERGY_STORAGE_METRICS bms_online rule.
  'bms_online',
] as const;

const SOLAR_METRIC_NAMES = [
  'solar_power_kw',
] as const;

const GENERATOR_METRIC_NAMES = [
  'generator_power_kw',
  'max_power_kw',
  'fuel_level',
  'engine_speed',
  'engine_load',
  'run_hours',
  'cumulative_energy',
  'engine_starts',
  'coolant_temperature',
  'oil_pressure',
  'battery_voltage',
  'slip_frequency',
  'sync_voltage_diff',
  'avg_line_voltage',
  'avg_current',
  'current_phase_a',
  'current_phase_b',
  'current_phase_c',
  'voltage_phase_a',
  'voltage_phase_b',
  'voltage_phase_c',
  'mains_breaker_closed',
] as const;

const METER_METRIC_NAMES = [
  'grid_power_kw',
  'grid_reactive_kvar',
  'grid_voltage_v',
  'voltage_phase_a',
  'voltage_phase_b',
  'voltage_phase_c',
  'current_phase_a',
  'current_phase_b',
  'current_phase_c',
  'reactive_power',
  'apparent_power',
  'meter_frequency',
] as const;

const SITE_METRIC_NAMES = [
  'total_load',
  'total_generation',
  'capacity_online',
  'capacity_offline',
  'bess_available',
  'bess_power',
  'fuel_consumption',
  'total_consumption',
  'grid_voltage',
  'grid_frequency',
  'total_active_power',
  'total_reactive_power',
  'average_soc',
  'total_apparent_power',
  'min_soc',
  'max_soc',
  'total_energy_capacity',
  'active_alarms',
  'critical_alarms',
  'warning_alarms',
  'total_blocks',
  'active_blocks',
  'faulted_blocks',
] as const;

export const ASSET_METRIC_CATALOG: Record<AssetType, readonly string[]> = {
  battery_storage: [...COMMON_METRIC_NAMES, ...BATTERY_METRIC_NAMES],
  solar_pv: [...COMMON_METRIC_NAMES, ...SOLAR_METRIC_NAMES],
  generator: [...COMMON_METRIC_NAMES, ...GENERATOR_METRIC_NAMES],
  meter: [...COMMON_METRIC_NAMES, ...METER_METRIC_NAMES],
  site: [...SITE_METRIC_NAMES],
};

function isKnownAssetType(type: string): type is AssetType {
  return type in ASSET_METRIC_CATALOG;
}

/**
 * Intersects the available-metrics list returned by the server with the
 * static catalog for the selected asset type. If assetType is empty or
 * unknown, returns the input unchanged.
 */
export function filterMetricsByAssetType(
  available: string[],
  assetType: string,
): string[] {
  if (!assetType || !isKnownAssetType(assetType)) return available;
  const allowed = new Set<string>(ASSET_METRIC_CATALOG[assetType]);
  return available.filter((metric) => allowed.has(metric));
}

/**
 * Unit string per metric name. Used as a fallback when the backend-returned
 * `columnMeta[col].unit` is null — happens on the continuous-aggregate rollup
 * path (`telemetry_1min`/`1hour`/`1day`), since continuous aggregates don't
 * materialize the `tags` column. Keep in sync with the unit values written
 * by `backend/src/services/telemetry-mapper.service.ts` rule arrays.
 *
 * Missing entries intentionally resolve to `null` at lookup time → the chart
 * falls back to single-axis rendering rather than guessing a wrong unit.
 */
export const METRIC_UNITS: Record<string, string> = {
  // Common / electrical
  active_power_kw: 'kW',
  reactive_power_kvar: 'kvar',
  apparent_power_kva: 'kVA',
  voltage_v: 'V',
  current_a: 'A',
  frequency: 'Hz',
  // power_factor intentionally omitted — dimensionless, resolves to null so
  // it doesn't spuriously create a right Y-axis when paired with a
  // dimensioned metric.
  max_reactive_capacity: 'kvar',
  min_reactive_capacity: 'kvar',
  comm_success_rate: '%',
  block_efficiency: '%',
  // Battery
  state_of_charge: '%',
  state_of_health: '%',
  battery_power_kw: 'kW',
  battery_temperature: '°C',
  cell_voltage_min: 'V',
  cell_voltage_max: 'V',
  cell_temp_min: '°C',
  cell_temp_max: '°C',
  max_power_kw: 'kW',
  min_power_kw: 'kW',
  pack_voltage: 'V',
  pack_current: 'A',
  max_charge_power: 'kW',
  max_discharge_power: 'kW',
  dc_bus_voltage: 'V',
  dc_bus_current: 'A',
  pcs_ac_voltage: 'V',
  pcs_ac_current: 'A',
  pcs_ac_frequency: 'Hz',
  // Solar
  solar_power_kw: 'kW',
  // Generator
  generator_power_kw: 'kW',
  fuel_level: '%',
  engine_speed: 'rpm',
  engine_load: '%',
  run_hours: 'h',
  cumulative_energy: 'kWh',
  coolant_temperature: '°C',
  oil_pressure: 'psi',
  battery_voltage: 'V',
  slip_frequency: 'Hz',
  sync_voltage_diff: 'V',
  avg_line_voltage: 'V',
  avg_current: 'A',
  current_phase_a: 'A',
  current_phase_b: 'A',
  current_phase_c: 'A',
  voltage_phase_a: 'V',
  voltage_phase_b: 'V',
  voltage_phase_c: 'V',
  // Meter
  grid_power_kw: 'kW',
  grid_reactive_kvar: 'kvar',
  grid_voltage_v: 'V',
  reactive_power: 'kvar',
  apparent_power: 'kVA',
  meter_frequency: 'Hz',
  // Site aggregates
  total_load: 'kW',
  total_generation: 'kW',
  capacity_online: 'kW',
  capacity_offline: 'kW',
  bess_available: 'kW',
  bess_power: 'kW',
  fuel_consumption: 'gal',
  total_consumption: 'kWh',
  grid_voltage: 'V',
  grid_frequency: 'Hz',
  total_active_power: 'kW',
  total_reactive_power: 'kvar',
  total_apparent_power: 'kVA',
  average_soc: '%',
  min_soc: '%',
  max_soc: '%',
  total_energy_capacity: 'kWh',
  // Generator fuel rate (MMBtu/h) — consumed by the canned historian reports
  // (djin-cloud#60) and the Generators card. Backend writes `fuel_rate` with
  // `unit: 'MMBtu/h'` in tags, so live telemetry paths already carry the unit;
  // this entry is the catalog fallback for the CAGG + canned-report paths
  // where unit is not materialized.
  fuel_rate: 'MMBtu/h',
  // Canned-report derived metrics (djin-cloud#60). The reports endpoint emits
  // peak_kw / kwh / fuel_mmbtu columns without per-row tags; these entries
  // give the chart + preview grid a consistent unit to render when the report
  // rows are charted or labeled.
  peak_kw: 'kW',
  energy_kwh: 'kWh',
  fuel_mmbtu: 'MMBtu',
};
